import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../services/api";
import {
  BackIcon,
  HomeIcon,
  CameraIcon,
  PlusIcon,
  UserIcon,
  LogoutIcon,
} from "../components/Icons";

function Account({ onLogout }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Email form
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  // Password form
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data } = await auth.me();
      setUser(data);
    } catch (err) {
      console.error("Failed to load user:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setEmailError("");
    setEmailSaving(true);

    try {
      await auth.updateEmail(newEmail);
      setUser({ ...user, email: newEmail });
      setShowEmailForm(false);
      setNewEmail("");
    } catch (err) {
      setEmailError(err.response?.data?.error || "Failed to update email");
    } finally {
      setEmailSaving(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    setPasswordSaving(true);

    try {
      await auth.updatePassword(newPassword);
      setShowPasswordForm(false);
      setNewPassword("");
      setConfirmPassword("");
      alert("Password updated successfully");
    } catch (err) {
      setPasswordError(
        err.response?.data?.error || "Failed to update password",
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "100px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Account</h1>
            <p className="header-subtitle">Manage your account settings</p>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Main Content Card */}
        <div className="card">
          {/* User Info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
              paddingBottom: "var(--spacing-lg)",
              borderBottom: "1px solid var(--border-color)",
              marginBottom: "var(--spacing-lg)",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--accent-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.25rem",
                color: "white",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {user?.email?.charAt(0).toUpperCase() || "?"}
            </div>
            <div style={{ fontWeight: 500, fontSize: "1rem" }}>
              {user?.email}
            </div>
          </div>

          {/* Email Section */}
          <div
            style={{
              paddingBottom: "var(--spacing-lg)",
              borderBottom: "1px solid var(--border-color)",
              marginBottom: "var(--spacing-lg)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: showEmailForm ? "var(--spacing-md)" : 0,
              }}
            >
              <div>
                <div style={{ fontWeight: 500, marginBottom: "2px" }}>
                  Email Address
                </div>
                <div className="text-muted" style={{ fontSize: "0.875rem" }}>
                  {user?.email}
                </div>
              </div>
              {!showEmailForm && (
                <button
                  onClick={() => setShowEmailForm(true)}
                  className="btn btn-secondary btn-sm"
                >
                  Change
                </button>
              )}
            </div>

            {showEmailForm && (
              <form onSubmit={handleUpdateEmail}>
                <div className="form-group">
                  <label>New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                </div>
                {emailError && (
                  <p
                    style={{
                      color: "var(--accent-danger)",
                      marginBottom: "var(--spacing-md)",
                    }}
                  >
                    {emailError}
                  </p>
                )}
                <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailForm(false);
                      setNewEmail("");
                      setEmailError("");
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={emailSaving}
                  >
                    {emailSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Password Section */}
          <div
            style={{
              paddingBottom: "var(--spacing-lg)",
              borderBottom: "1px solid var(--border-color)",
              marginBottom: "var(--spacing-lg)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: showPasswordForm ? "var(--spacing-md)" : 0,
              }}
            >
              <div>
                <div style={{ fontWeight: 500, marginBottom: "2px" }}>
                  Password
                </div>
                <div className="text-muted" style={{ fontSize: "0.875rem" }}>
                  ••••••••
                </div>
              </div>
              {!showPasswordForm && (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="btn btn-secondary btn-sm"
                >
                  Change
                </button>
              )}
            </div>

            {showPasswordForm && (
              <form onSubmit={handleUpdatePassword}>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                {passwordError && (
                  <p
                    style={{
                      color: "var(--accent-danger)",
                      marginBottom: "var(--spacing-md)",
                    }}
                  >
                    {passwordError}
                  </p>
                )}
                <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setNewPassword("");
                      setConfirmPassword("");
                      setPasswordError("");
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={passwordSaving}
                  >
                    {passwordSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Sign Out */}
          <button
            onClick={handleLogout}
            className="btn btn-ghost"
            style={{
              color: "var(--accent-danger)",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: "var(--spacing-sm)",
            }}
          >
            <LogoutIcon size={18} /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default Account;
